import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { PickUpConditionListType } from 'types/pickUpConditions';
import {
  HolidayAndWayOfWorkingListType,
  RequirementListType,
} from 'types/kodawari';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import {
  JobCountResponse,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { changeOrderPickUpRequirement } from 'logics/changeOrderPickUpRequirement/changeOrderPickUpRequirement';
import {
  createInitialIsNewArrivalData,
  createInitialIsNearDeadline,
} from '../../logics/createInitialPickUpConditionList';
import { SEARCH_PICKUP_CONDITION_OPTIONS } from '../../../../../constants/searchCondition';

interface ArgumentType {
  holidayAndWayOfWorkingList: HolidayAndWayOfWorkingListType[];
  savedSearchCondition?: SavedSearchResponse | undefined;
  featuredSearchCondition?: FeaturedSearchConditionQuery | undefined;
  requirementList: RequirementListType[];
  jobCountData: JobCountResponse;
}

/**
 * こだわり条件ピックアップに関するhooks
 * @param param.holidayAndWayOfWorkingList 休日・働き方の配列
 * @param param.savedSearchCondition 保存した検索条件のレスポンス
 * @param param.featuredSearchCondition 特集枠検索条件のレスポンス
 * @param param.requirementList 募集・採用情報の配列
 * @param param.jobCountData 求人件数
 * @returns 表示用配列と更新処理
 */
export const usePickUpCondition = ({
  holidayAndWayOfWorkingList,
  savedSearchCondition,
  featuredSearchCondition,
  requirementList,
  jobCountData,
}: ArgumentType) => {
  const { op } = useAppSelector(appQueryParamsSelector);

  const [newArrivalData, setNewArrivalData] = useState<PickUpConditionListType>(
    createInitialIsNewArrivalData(
      jobCountData.newArrivalCount ?? 0,
      op,
      savedSearchCondition,
      featuredSearchCondition
    )
  );
  const [nearDeadlineData, setNearDeadlineData] =
    useState<PickUpConditionListType>(
      createInitialIsNearDeadline(
        jobCountData.nearDeadlineCount ?? 0,
        op,
        savedSearchCondition,
        featuredSearchCondition
      )
    );

  const pickUpHolidayAndWayOfWorkingList: PickUpConditionListType[] =
    useMemo(() => {
      return holidayAndWayOfWorkingList
        .filter((item) =>
          SEARCH_PICKUP_CONDITION_OPTIONS.list.some(({ id }) => id === item.id)
        )
        .map((item) => ({
          ...item,
          type: 'holidayAndWayOfWorking',
        }));
    }, [holidayAndWayOfWorkingList]);

  const pickUpRequirementList: PickUpConditionListType[] = useMemo(() => {
    return requirementList
      .filter((item) =>
        SEARCH_PICKUP_CONDITION_OPTIONS.list.some(({ id }) => id === item.id)
      )
      .map((item) => ({
        ...item,
        type: 'requirement',
      }));
  }, [requirementList]);

  const pickUpConditionDisplayList: PickUpConditionListType[] = useMemo(() => {
    return [
      newArrivalData,
      nearDeadlineData,
      ...pickUpHolidayAndWayOfWorkingList,
      ...changeOrderPickUpRequirement(pickUpRequirementList),
    ];
  }, [
    nearDeadlineData,
    newArrivalData,
    pickUpHolidayAndWayOfWorkingList,
    pickUpRequirementList,
  ]);

  const isNewArrival = newArrivalData.isChecked;
  const isNearDeadline = nearDeadlineData.isChecked;

  // 新着のチェック状態更新処理
  const updateIsCheckedIsNewArrival = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checkedObject = {
        ...newArrivalData,
        isChecked: event.target.checked,
      };

      setNewArrivalData(checkedObject);
    },
    [newArrivalData]
  );

  // 締切間近のチェック状態更新処理
  const updateIsCheckedIsNearDeadline = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checkedObject = {
        ...nearDeadlineData,
        isChecked: event.target.checked,
      };

      setNearDeadlineData(checkedObject);
    },
    [nearDeadlineData]
  );

  // 新着の求人件数更新処理
  const updateNewArrivalJobCount = useCallback(
    (newArrivalCount: number | undefined) => {
      setNewArrivalData((pre) => ({
        ...pre,
        jobCount: newArrivalCount ?? 0,
      }));
    },
    []
  );

  // 締切間近の求人件数更新処理
  const updateNearDeadlineJobCount = useCallback(
    (nearDeadlineJobCount: number | undefined) => {
      setNearDeadlineData((pre) => ({
        ...pre,
        jobCount: nearDeadlineJobCount ?? 0,
      }));
    },
    []
  );

  return {
    isNewArrival,
    isNearDeadline,
    pickUpConditionDisplayList,
    updateIsCheckedIsNewArrival,
    updateIsCheckedIsNearDeadline,
    updateNewArrivalJobCount,
    updateNearDeadlineJobCount,
  };
};
